import React from 'react';
import { Lock, Globe } from 'lucide-react';

/**
 * WHO CAN JOIN. Two states, named in player language.
 *
 * "Invite only" and "Anyone can join" — not "Public/Private", which is system
 * language that makes a person stop and translate, and certainly not
 * "isPrivate". The host is deciding whether strangers may sit down at a table
 * with their kids; the words have to be the words they'd use themselves.
 *
 * When the room is open it SAYS SO, in the same breath and without being asked:
 * "Listed in Public Games — anyone can walk in." A host must never be surprised
 * about who can see their family. It takes the accent border in that state for
 * the same reason — this is the one setting in the lobby that carries
 * consequences, and it should look like it.
 *
 * NON-HOSTS SEE IT TOO, read-only. They cannot change it, and no toggle is
 * rendered for them — a disabled control the server would refuse anyway is an
 * invitation to tap something that cannot work. But the STATE is theirs to
 * know: a player has a right to know the room they are sitting in has been
 * listed to strangers, and hiding that to keep the lobby tidy would mean the
 * only person who knows is the host.
 *
 * Stateless by construction. `isPrivate` comes from the room's own settings
 * (server-owned, via `gameSettingsUpdated`), never from a copy this component
 * keeps — a control with its own private idea of the truth is how a lobby ends
 * up showing "Invite only" over a room that is listed to the world.
 */
export function VisibilityCard({
  isPrivate,
  isHost,
  onChange,
}: {
  isPrivate: boolean;
  isHost: boolean;
  /** Called with the NEXT isPrivate. The caller sends it to the server. */
  onChange: (nextIsPrivate: boolean) => void;
}) {
  const open = !isPrivate;
  return (
    <div
      className="card wr-between"
      style={{
        padding: 14,
        gap: 12,
        alignItems: 'center',
        border: open ? '1.5px solid var(--accent)' : undefined,
        background: open ? 'var(--accent-soft)' : undefined,
      }}
    >
      <div className="wr-row gap-3" style={{ alignItems: 'center', minWidth: 0 }}>
        <span
          className="wr-center"
          style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            flexShrink: 0,
            background: open ? 'var(--accent)' : 'var(--surface-2)',
            color: open ? '#fff' : 'var(--ink-3)',
          }}
        >
          {open ? <Globe size={16} strokeWidth={2.4} /> : <Lock size={16} strokeWidth={2.4} />}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {open ? 'Anyone can join' : 'Invite only'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.4 }}>
            {open
              ? 'Listed in Public Games — anyone can walk in.'
              : 'Only people with this code or link can join.'}
          </div>
        </div>
      </div>
      {isHost ? (
        <button
          className={`wr-toggle ${open ? 'on' : ''}`}
          onClick={() => onChange(!isPrivate)}
          type="button"
          aria-pressed={open}
          aria-label="Anyone can join"
          style={{ flexShrink: 0 }}
        />
      ) : (
        <span style={{ fontSize: 12, color: 'var(--ink-3)', flexShrink: 0, whiteSpace: 'nowrap' }}>
          Host decides
        </span>
      )}
    </div>
  );
}

export default VisibilityCard;
